import { useCallback, useContext, useEffect, useRef, useState } from "react";
import { EditorStoreContext } from "../store/EditorStoreContext";

/**
 * Pointer-driven drag for anything on the canvas (mouse, pen, or touch).
 *
 * - Deltas arrive in page units: screen movement is divided by the canvas
 *   zoom, so drags track the cursor at any zoom level.
 * - Moves are coalesced to one per animation frame using the *latest*
 *   pointer position, and the release position is always applied, so items
 *   land exactly where they are let go.
 *
 * Usage:
 *   const drag = usePointerDrag({ onMove, onEnd });
 *   <div onPointerDown={(e) => drag.start(e, { kind: "move", ...startState })} />
 *   drag.active // the payload of the drag in progress, or null
 *   drag.moving // true once the pointer has actually moved (not just a click)
 *
 * onMove({ dx, dy, event, payload }) and onEnd({ moved, payload }).
 * `getScale` overrides the zoom lookup (defaults to the editor store's zoom).
 */
export const usePointerDrag = ({ onMove, onEnd, getScale } = {}) => {
  const store = useContext(EditorStoreContext);
  const handlersRef = useRef({ onMove, onEnd, getScale });
  handlersRef.current = { onMove, onEnd, getScale };

  const sessionRef = useRef(null);
  const [active, setActive] = useState(null);
  const [moving, setMoving] = useState(false);

  // Listeners are attached synchronously in start(), never in an effect, so
  // a release that arrives before React re-renders can't be missed (which
  // would leave the drag — and its undo gesture — stuck open).
  const start = useCallback((event, payload = {}) => {
    if (event.button != null && event.button !== 0 && event.pointerType === "mouse") return;
    if (sessionRef.current) sessionRef.current.cancel();

    const scaleGetter = handlersRef.current.getScale;
    const rawScale = scaleGetter ? scaleGetter() : store?.getState().zoom || 1;
    const session = {
      pointerId: event.pointerId,
      startX: event.clientX,
      startY: event.clientY,
      scale: rawScale > 0 ? rawScale : 1,
      payload,
    };
    let frame = null;
    let latest = null;
    let hasMoved = false;

    const emit = (e) => {
      if (!hasMoved) {
        hasMoved = true;
        setMoving(true);
      }
      if (!handlersRef.current.onMove) return;
      handlersRef.current.onMove({
        dx: (e.clientX - session.startX) / session.scale,
        dy: (e.clientY - session.startY) / session.scale,
        event: e,
        payload,
      });
    };

    const isOurs = (e) => session.pointerId == null || e.pointerId == null || e.pointerId === session.pointerId;

    const handleMove = (e) => {
      if (!isOurs(e)) return;
      latest = e;
      if (frame) return;
      frame = requestAnimationFrame(() => {
        frame = null;
        if (latest) emit(latest);
      });
    };

    const previousUserSelect = document.body.style.userSelect;
    const detach = () => {
      if (frame) cancelAnimationFrame(frame);
      frame = null;
      document.body.style.userSelect = previousUserSelect;
      window.removeEventListener("pointermove", handleMove);
      window.removeEventListener("pointerup", handleUp);
      window.removeEventListener("pointercancel", handleCancel);
      if (sessionRef.current === session) sessionRef.current = null;
    };

    const finish = (e, cancelled) => {
      const moved = latest != null;
      // Land exactly at the release point
      if (moved && !cancelled && e) emit(e);
      detach();
      setActive(null);
      setMoving(false);
      if (handlersRef.current.onEnd) handlersRef.current.onEnd({ moved, payload });
    };

    function handleUp(e) {
      if (isOurs(e)) finish(e, false);
    }
    function handleCancel(e) {
      if (isOurs(e)) finish(e, true);
    }

    session.cancel = () => finish(null, true);
    sessionRef.current = session;
    // Keep the browser from selecting text while something is being dragged
    document.body.style.userSelect = "none";
    window.addEventListener("pointermove", handleMove);
    window.addEventListener("pointerup", handleUp);
    window.addEventListener("pointercancel", handleCancel);
    setMoving(false);
    setActive(payload);
  }, [store]);

  // Unmounting mid-drag ends the drag
  useEffect(() => () => {
    if (sessionRef.current) sessionRef.current.cancel();
  }, []);

  return { start, active, moving };
};
